import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { IconChevronRight } from "@tabler/icons-react";

import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { DataTable } from "@/components/ui/data-table";
import {
  fetchPerformanceAdset,
  fetchPerformanceCampaign,
  fetchPerformanceCampaigns,
  type CurrencyView,
  type ObjectView,
} from "@/services/metaPerformance.service";
import { cn } from "@/lib/utils";

import type { PerfScope } from "./AdPerformancePage";
import { performanceColumns } from "./columns";
import { fmtInt, fmtMoney, fmtPct, fmtRoas } from "./format";
import { BucketText, MixedMoney } from "./bits";

/**
 * Campaign → ad set → ad, one table narrowing at each step with a breadcrumb
 * to climb back. Clicking an ad opens its detail sheet.
 */
export function CampaignsTab({ scope, onCurrency, focusCampaignId }: { scope: PerfScope; onCurrency: (c: CurrencyView) => void; focusCampaignId: string | null }) {
  const [campaignId, setCampaignId] = useState<string | null>(focusCampaignId);
  const [adsetId, setAdsetId] = useState<string | null>(null);
  useEffect(() => {
    if (focusCampaignId) {
      setCampaignId(focusCampaignId);
      setAdsetId(null);
    }
  }, [focusCampaignId]);

  const list = useQuery({
    queryKey: ["meta-performance", "campaigns", scope.days, scope.reveshareOnly],
    queryFn: () => fetchPerformanceCampaigns(scope.days, scope.reveshareOnly),
  });
  const campaign = useQuery({
    queryKey: ["meta-performance", "campaign", campaignId, scope.days],
    queryFn: () => fetchPerformanceCampaign(campaignId!, scope.days),
    enabled: !!campaignId,
  });
  const adset = useQuery({
    queryKey: ["meta-performance", "adset", adsetId, scope.days],
    queryFn: () => fetchPerformanceAdset(adsetId!, scope.days),
    enabled: !!adsetId,
  });
  useEffect(() => {
    if (list.data) onCurrency(list.data.currency);
  }, [list.data, onCurrency]);

  const currency = list.data?.currency.account ?? "";
  const campaignCols = useMemo(() => performanceColumns("campaign", currency), [currency]);
  const adsetCols = useMemo(() => performanceColumns("adset", currency), [currency]);
  const adCols = useMemo(() => performanceColumns("ad", currency), [currency]);

  const crumbCampaign = campaign.data?.campaign ?? list.data?.campaigns.find((c) => c.id === campaignId) ?? null;
  const crumbAdset = adset.data?.adset ?? campaign.data?.adsets.find((a) => a.id === adsetId) ?? null;

  const level: "campaign" | "adset" | "ad" = adsetId ? "ad" : campaignId ? "adset" : "campaign";
  const rows: ObjectView[] = level === "ad" ? adset.data?.ads ?? [] : level === "adset" ? campaign.data?.adsets ?? [] : list.data?.campaigns ?? [];
  const loading = level === "ad" ? adset.isLoading : level === "adset" ? campaign.isLoading : list.isLoading;
  const parent: ObjectView | null = level === "ad" ? crumbAdset : level === "adset" ? crumbCampaign : null;

  return (
    <div className="space-y-4">
      <nav aria-label="Drill-down" className="flex flex-wrap items-center gap-1 text-sm">
        <Crumb active={level === "campaign"} onClick={() => { setCampaignId(null); setAdsetId(null); }}>All campaigns</Crumb>
        {campaignId && (
          <>
            <IconChevronRight size={14} className="text-muted-foreground" aria-hidden />
            <Crumb active={level === "adset"} onClick={() => setAdsetId(null)}>{crumbCampaign?.name ?? "Campaign"}</Crumb>
          </>
        )}
        {adsetId && (
          <>
            <IconChevronRight size={14} className="text-muted-foreground" aria-hidden />
            <Crumb active>{crumbAdset?.name ?? "Ad set"}</Crumb>
          </>
        )}
      </nav>

      {parent && <ParentStrip object={parent} currency={currency} />}

      <DashboardCard
        title={level === "campaign" ? "Campaign breakdown" : level === "adset" ? "Ad sets in this campaign" : "Ads in this ad set"}
        bodyClassName="px-0 pb-0"
      >
        <DataTable
          columns={level === "ad" ? adCols : level === "adset" ? adsetCols : campaignCols}
          data={rows}
          isLoading={loading}
          getRowId={(r) => r.id}
          emptyText={
            level === "campaign"
              ? scope.reveshareOnly
                ? "No campaigns with a Reveshare-launched ad in this period"
                : "No campaigns delivered in this period"
              : level === "adset"
                ? "No ad sets in this campaign"
                : "No ads in this ad set"
          }
          onRowClick={(r) => {
            if (level === "campaign") setCampaignId(r.id);
            else if (level === "adset") setAdsetId(r.id);
            else scope.openAd(r.id);
          }}
        />
      </DashboardCard>
    </div>
  );
}

function Crumb({ active, onClick, children }: { active?: boolean; onClick?: () => void; children: React.ReactNode }) {
  if (active || !onClick) return <span className="max-w-[18rem] truncate font-medium text-foreground">{children}</span>;
  return (
    <button type="button" onClick={onClick} className="max-w-[18rem] truncate text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
      {children}
    </button>
  );
}

/** The parent's own figures while its children are listed below. */
function ParentStrip({ object, currency }: { object: ObjectView; currency: string }) {
  const m = object.meta;
  const cells: Array<{ label: string; value: React.ReactNode }> = [
    { label: "Spend", value: fmtMoney(m.spend, currency) },
    { label: "Revenue (Meta)", value: fmtMoney(m.purchaseValue, currency) },
    { label: "ROAS", value: <BucketText bucket={object.bucket} roas={fmtRoas(m.roas)} /> },
    { label: "Purchases", value: fmtInt(m.purchases) },
    { label: "CTR", value: fmtPct(m.ctr) },
    { label: "CPC", value: m.cpc != null ? fmtMoney(m.cpc, currency) : "—" },
    { label: "Reveshare revenue", value: object.reveshare.orders ? <MixedMoney m={object.reveshare.revenue} fallbackCurrency={currency} /> : "—" },
    { label: "Daily budget", value: object.dailyBudget ? fmtMoney(object.dailyBudget.amount, object.dailyBudget.currency) : "—" },
  ];
  return (
    <div className={cn("-ml-px grid grid-cols-2 overflow-hidden rounded-lg border border-border bg-card sm:grid-cols-4 lg:grid-cols-8")}>
      {cells.map((c) => (
        <div key={c.label} className="flex min-w-0 flex-col gap-0.5 border-l border-t border-border px-4 py-3 first:border-t-0 sm:border-t-0">
          <span className="text-[11px] font-medium text-muted-foreground">{c.label}</span>
          <span className="truncate text-base font-semibold tabular-nums text-foreground">{c.value}</span>
        </div>
      ))}
    </div>
  );
}
